import React, { useEffect, useState } from 'react';
import {
  Activity,
  BookOpen,
  Building2,
  Coins,
  Hash,
  Landmark,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Scale,
  Search,
  TrendingDown,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { RowAction, editAction, viewAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { BankAccountTiles } from '../components/BankAccountTiles';
import { BankReconciliationForm } from '../forms/BankReconciliationForm';
import FinancialSuite from '../FinancialSuite';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailIdentity
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import {
  BankAccountRecord,
  BankMovement,
  createBankAccountWithAccount,
  listBankAccounts,
  listBankMovements,
  updateBankAccount
} from '../../../../../services/BankAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import '../../shared-modules.css';
import './FinanceMasters.css';

// Cuenta PGC con la que se contabilizan los bancos que aún no tienen subcuenta 572 enlazada
const DEFAULT_BANK_PGC_ACCOUNT = '572000';

// keep = dejar la subcuenta como está (solo al editar)
type CreateMode = 'keep' | 'existing' | 'new';

interface BankForm {
  mode: CreateMode;
  accountId: string;
  code: string;
  name: string;
  iban: string;
  currency: string;
  balance: string;
  status: 'active' | 'inactive';
}

const EMPTY_FORM: BankForm = { mode: 'existing', accountId: '', code: '', name: '', iban: '', currency: 'EUR', balance: '0', status: 'active' };

const formatMoney = (val: number, currency: string, privacyMode: boolean) => {
  if (privacyMode) return '•••••';
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
  return `${val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
};

interface FinanceBankAccountsViewProps {
  privacyMode: boolean;
}

export const FinanceBankAccountsView = ({ privacyMode }: FinanceBankAccountsViewProps) => {
  const { notify } = useToast();

  const [accounts, setAccounts] = useState<BankAccountRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [chartAccounts, setChartAccounts] = useState<ChartAccount[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<BankForm>(EMPTY_FORM);
  const [editing, setEditing] = useState<BankAccountRecord | null>(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Sube tras crear, editar o cambiar el estado: las pastillas vuelven a pedir los datos
  const [tilesKey, setTilesKey] = useState(0);
  // Cuenta cuya conciliación bancaria (y carga del Cuaderno 43) está abierta bajo las pastillas
  const [reconBankId, setReconBankId] = useState('');
  // Cuenta desde la que se abre la remesa de Cobros SEPA (Norma 19)
  const [sepaBankId, setSepaBankId] = useState('');

  const [selectedAccount, setSelectedAccount] = useState<BankAccountRecord | null>(null);
  const [movements, setMovements] = useState<BankMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const list = await listBankAccounts();
      setAccounts(list);
      setListError('');
      setSelectedAccount(prev => (prev ? list.find(a => a.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setAccounts([]);
      setSelectedAccount(null);
      setListError(err instanceof Error ? err.message : 'No se pudieron cargar las cuentas bancarias.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    listChartOfAccounts()
      .then(setChartAccounts)
      .catch(err => console.error('Error fetching chart of accounts:', err));
  }, []);

  // Código de la subcuenta 572 enlazada a un banco; sin enlace, la 572000 por defecto
  const pgcCodeOf = (a: BankAccountRecord) =>
    a.accountCode || chartAccounts.find(c => c.id === a.accountId)?.code || (a.accountId ? `#${a.accountId}` : DEFAULT_BANK_PGC_ACCOUNT);

  // Subcuentas 572 del plan que todavía no tienen banco
  const freeBankSubaccounts = chartAccounts.filter(c =>
    c.level === 'subaccount' && c.code.startsWith('572') && !accounts.some(a => a.accountId === c.id && a.id !== editing?.id)
  );

  const setField = <K extends keyof BankForm>(field: K, value: BankForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleEditClick = (a: BankAccountRecord) => {
    setReconBankId('');
    setSepaBankId('');
    setSelectedAccount(a);
    setEditing(a);
    setFormData({ ...EMPTY_FORM, mode: 'keep', name: a.name, iban: a.iban, currency: a.currency, balance: String(a.balance), status: a.status === 'inactive' ? 'inactive' : 'active' });
    setFormError('');
    setIsFormOpen(true);
  };

  const handleCreateNewClick = () => {
    setReconBankId('');
    setSepaBankId('');
    setEditing(null);
    setSelectedAccount(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setIsFormOpen(true);
  };

  const handlePickSubaccount = (id: string) => {
    const acc = chartAccounts.find(c => c.id === id);
    // El nombre del banco parte del de la subcuenta, pero se puede cambiar
    setFormData(prev => ({ ...prev, accountId: id, name: prev.name.trim() ? prev.name : acc?.name || '' }));
  };

  const handleSaveEdit = async (name: string, code: string) => {
    if (formData.mode === 'existing' && !formData.accountId) { setFormError('Elige la subcuenta 572 a enlazar.'); return; }
    if (formData.mode === 'new') {
      if (!/^572[0-9]{1,7}$/.test(code)) { setFormError('Una subcuenta de bancos empieza por 572 y tiene entre 4 y 10 dígitos (p. ej. 5720001).'); return; }
      if (chartAccounts.some(c => c.code === code)) { setFormError(`Ya existe una cuenta con el código ${code}.`); return; }
    }
    if (!/^[A-Za-z0-9 ]{0,34}$/.test(formData.iban.trim())) { setFormError('El IBAN solo admite letras y números (máx. 34 caracteres).'); return; }

    setIsSaving(true);
    try {
      const updated = await updateBankAccount(editing!.id, {
        name,
        iban: formData.iban.trim(),
        status: formData.status,
        ...(formData.mode === 'existing' ? { accountId: formData.accountId } : {}),
        ...(formData.mode === 'new' ? { newAccountCode: code } : {})
      });
      setIsFormOpen(false);
      setEditing(null);
      const [list, chart] = await Promise.all([listBankAccounts(), listChartOfAccounts()]);
      setAccounts(list);
      setChartAccounts(chart);
      setTilesKey(k => k + 1);
      setSelectedAccount(list.find(a => a.id === updated.id) || null);
      notify('Éxito', `Cuenta bancaria "${updated.name}" actualizada.`, 'info');
    } catch (err) {
      console.error('Error updating bank account:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAccount = async () => {
    const name = formData.name.trim();
    const code = formData.code.trim();
    if (!name) { setFormError('El nombre del banco es obligatorio.'); return; }
    if (editing) { await handleSaveEdit(name, code); return; }
    if (formData.mode === 'existing' && !formData.accountId) { setFormError('Elige la subcuenta 572 a enlazar.'); return; }
    if (formData.mode === 'new') {
      if (!/^572[0-9]{1,7}$/.test(code)) { setFormError('Una subcuenta de bancos empieza por 572 y tiene entre 4 y 10 dígitos (p. ej. 5720001).'); return; }
      if (chartAccounts.some(c => c.code === code)) { setFormError(`Ya existe una cuenta con el código ${code}.`); return; }
    }
    if (!/^[A-Za-z0-9 ]{0,34}$/.test(formData.iban.trim())) { setFormError('El IBAN solo admite letras y números (máx. 34 caracteres).'); return; }
    const currency = formData.currency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) { setFormError('La moneda son 3 letras (EUR, USD…).'); return; }
    const balance = Number(formData.balance.replace(',', '.'));
    if (formData.balance.trim() === '' || isNaN(balance)) { setFormError('El saldo inicial no es un número válido.'); return; }

    setIsSaving(true);
    try {
      const created = await createBankAccountWithAccount({
        name,
        iban: formData.iban.trim(),
        currency,
        balance,
        ...(formData.mode === 'existing' ? { accountId: formData.accountId } : { code })
      });
      setIsFormOpen(false);
      const [list, chart] = await Promise.all([listBankAccounts(), listChartOfAccounts()]);
      setAccounts(list);
      setChartAccounts(chart);
      setTilesKey(k => k + 1);
      setSelectedAccount(list.find(a => a.id === created.id) || null);
      notify('Éxito', `Cuenta bancaria "${created.name}" creada y enlazada a la subcuenta ${created.accountCode}.`, 'info');
    } catch (err) {
      console.error('Error creating bank account:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!selectedAccount) return;
    let cancelled = false;
    setMovementsLoading(true);
    listBankMovements(selectedAccount.id)
      .then(list => { if (!cancelled) setMovements(list); })
      .catch(err => {
        console.error('Error fetching bank movements:', err);
        if (!cancelled) setMovements([]);
      })
      .finally(() => { if (!cancelled) setMovementsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedAccount?.id]);

  const handleSync = async () => {
    await fetchAccounts();
    notify('Sincronización bancaria', 'Cuentas bancarias actualizadas.', 'info');
  };

  const closePanel = () => { setSelectedAccount(null); setIsFormOpen(false); setEditing(null); };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedAccount) && !isFormOpen);

  const isSynced = (a: BankAccountRecord) => a.status === 'active';

  // Saldo contable: lo que suman los apuntes de la subcuenta 572 enlazada. Sin subcuenta no hay saldo contable propio.
  const accountingBalance = (a: BankAccountRecord): number | null => a.ledgerBalance;

  // Por qué no hay saldo contable: sin subcuenta enlazada, o el servidor no lo devolvió
  const noLedgerText = (a: BankAccountRecord) => (a.accountId ? 'No disponible' : 'Sin subcuenta enlazada');

  // Acciones del menú ⋮ de cada pastilla
  const recordOf = (tile: BankAccountRecord) => accounts.find(x => x.id === tile.id) || tile;

  const openSepa = (tile: BankAccountRecord) => {
    setIsFormOpen(false);
    setEditing(null);
    setSelectedAccount(null);
    setReconBankId('');
    setSepaBankId(tile.id);
  };

  const closeSepa = () => {
    setSepaBankId('');
    setTilesKey(k => k + 1);
  };

  const openRecon = (tile: BankAccountRecord) => {
    setSepaBankId('');
    setIsFormOpen(false);
    setEditing(null);
    setSelectedAccount(null);
    setReconBankId(tile.id);
  };

  const closeRecon = () => {
    setReconBankId('');
    setTilesKey(k => k + 1);
  };

  const openFicha = (tile: BankAccountRecord) => {
    setReconBankId('');
    setSepaBankId('');
    setIsFormOpen(false);
    setEditing(null);
    setSelectedAccount(recordOf(tile));
  };

  const handleToggleStatus = async (tile: BankAccountRecord) => {
    const next = tile.status === 'active' ? 'inactive' : 'active';
    try {
      await updateBankAccount(tile.id, { name: tile.name, iban: tile.iban, status: next });
      const list = await listBankAccounts();
      setAccounts(list);
      setSelectedAccount(prev => (prev ? list.find(x => x.id === prev.id) || null : null));
      setTilesKey(k => k + 1);
      notify('Éxito', `Cuenta "${tile.name}" ${next === 'active' ? 'activada' : 'desactivada'}.`, 'info');
    } catch (err) {
      notify('Cuentas bancarias', (err as Error)?.message || 'No se pudo cambiar el estado.', 'warning');
    }
  };

  const menuActionsFor = (tile: BankAccountRecord): RowAction[] => [
    viewAction(() => openFicha(tile)),
    editAction(() => handleEditClick(recordOf(tile))),
    {
      label: 'Conciliación bancaria',
      icon: Scale,
      tone: 'accent',
      title: 'Cargar el Cuaderno 43 y conciliar los movimientos de esta cuenta',
      onSelect: () => openRecon(tile)
    },
    {
      label: 'Cobros SEPA (Norma 19)',
      icon: Coins,
      tone: 'accent',
      title: 'Remesa de adeudos directos de esta cuenta',
      onSelect: () => openSepa(tile)
    },
    {
      label: tile.status === 'active' ? 'Desactivar' : 'Activar',
      icon: Power,
      tone: tile.status === 'active' ? 'danger' : 'accent',
      onSelect: () => handleToggleStatus(tile)
    }
  ];

  const renderMovementsSkeleton = () => (
    <div className="fin-master__attachments">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="fin-master__attachment">
          <div className="skeleton-block skeleton-block--tight" style={{ flex: 1 }}>
            <Skeleton width="60%" height="10px" />
            <Skeleton width="30%" height="8px" />
          </div>
          <Skeleton width="60px" height="10px" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR NOMBRE, MONEDA O ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nueva_Cuenta
        </button>
        <button
          onClick={handleSync}
          className="module-create-btn"
          disabled
          title="Sincronización bancaria desactivada"
          data-keep-detail
        >
          <RefreshCw size={14} /> Sincronizar_Cuentas
        </button>
      </div>

      {listError && (
        <div className="module-panel">
          <div className="module-panel-empty">{listError}</div>
        </div>
      )}

      <BankAccountTiles
        privacyMode={privacyMode}
        refreshKey={String(tilesKey)}
        filter={searchQuery}
        menuActions={menuActionsFor}
      >
        <div className="bank-detail-area">
        {isFormOpen ? (
          <SidebarForm
            expandable
            title={editing ? 'Editar_Cuenta_Bancaria' : 'Alta_De_Cuenta_Bancaria'}
            onClose={closePanel}
            submitLabel={isSaving ? 'Guardando...' : editing ? 'Guardar' : 'Crear_Cuenta'}
            submitDisabled={isSaving}
            onSubmit={handleSaveAccount}
          >
            {formError && <div className="module-form-error"><span>{formError}</span></div>}

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Subcuenta contable (572) *</label>
              <select value={formData.mode} onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value as CreateMode, accountId: '', code: '' }))} className="sidebar-form-input">
                {editing && (
                  <option value="keep">
                    {editing.accountId ? `Mantener la actual (${pgcCodeOf(editing)})` : `Sin subcuenta enlazada (se usa ${DEFAULT_BANK_PGC_ACCOUNT})`}
                  </option>
                )}
                <option value="existing">{editing ? 'Cambiar a otra subcuenta 572 existente' : 'Enlazar una subcuenta 572 existente'}</option>
                <option value="new">{editing ? 'Crear una subcuenta 572 nueva y enlazarla' : 'Crear una subcuenta 572 nueva'}</option>
              </select>
            </div>

            {formData.mode === 'keep' ? null : formData.mode === 'existing' ? (
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Subcuenta *</label>
                <select value={formData.accountId} onChange={(e) => handlePickSubaccount(e.target.value)} className="sidebar-form-input">
                  <option value="">Selecciona una subcuenta...</option>
                  {freeBankSubaccounts.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                </select>
                {freeBankSubaccounts.length === 0 && (
                  <p className="fin-master__sub">No quedan subcuentas 572 sin banco. Crea una nueva.</p>
                )}
              </div>
            ) : (
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Código de la subcuenta *</label>
                <input type="text" value={formData.code} onChange={(e) => setField('code', e.target.value)} placeholder="Ej. 5720001" className="sidebar-form-input" />
              </div>
            )}

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Nombre del banco *</label>
              <input type="text" value={formData.name} onChange={(e) => setField('name', e.target.value)} placeholder="Ej. Banco Sabadell Madrid" className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">IBAN</label>
              <input type="text" value={formData.iban} maxLength={34} onChange={(e) => setField('iban', e.target.value.toUpperCase())} placeholder="ES00 0000 0000 0000 0000 0000" className="sidebar-form-input" />
            </div>
            {editing ? (
              <>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select value={formData.status} onChange={(e) => setField('status', e.target.value as 'active' | 'inactive')} className="sidebar-form-input">
                    <option value="active">ACTIVA</option>
                    <option value="inactive">INACTIVA</option>
                  </select>
                </div>
                <p className="fin-master__sub">
                  Moneda: {editing.currency} · Saldo: {formatMoney(editing.balance, editing.currency, privacyMode)}. No se editan aquí: el saldo lo mueven los movimientos bancarios.
                </p>
              </>
            ) : (
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Moneda *</label>
                  <input type="text" value={formData.currency} maxLength={3} onChange={(e) => setField('currency', e.target.value.toUpperCase())} className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Saldo inicial *</label>
                  <input type="number" step="0.01" value={formData.balance} onChange={(e) => setField('balance', e.target.value)} className="sidebar-form-input right" />
                </div>
              </div>
            )}
          </SidebarForm>
        ) : selectedAccount ? (
          <DetailPanel
            expandable
            title="Ficha_De_Cuenta_Bancaria"
            onClose={closePanel}
            headerActions={
              <button type="button" className="module-action-btn" onClick={() => handleEditClick(selectedAccount)} data-keep-detail>
                <Pencil size={12} /> Editar
              </button>
            }
            cards={
              <DetailCard
                icon={Activity}
                title={`Últimos_Movimientos ${movementsLoading ? '' : `(${movements.length})`}`}
              >
                {movementsLoading ? (
                  renderMovementsSkeleton()
                ) : movements.length === 0 ? (
                  <div className="fin-master__empty">Sin movimientos registrados</div>
                ) : (
                  <div className="fin-master__attachments">
                    {[...movements].sort((x, y) => (y.date.localeCompare(x.date)) || (Number(y.id) - Number(x.id))).slice(0, 10).map(m => (
                      <div key={m.id} className="fin-master__attachment">
                        <div>
                          <div className="fin-master__text">{m.description}</div>
                          <div className="fin-master__sub">{m.date || '—'}</div>
                        </div>
                        <span className={`fin-master__movement ${m.type}`}>
                          {m.type === 'income' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {formatMoney(m.amount, selectedAccount.currency, privacyMode)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={Building2}
              reference={`ID ${selectedAccount.id}`}
              name={selectedAccount.name}
              subtitle={
                <span className={`module-badge ${isSynced(selectedAccount) ? 'active' : 'draft'}`}>
                  {isSynced(selectedAccount) ? 'Sincronizado' : 'Revisar'}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={BookOpen} label="CUENTA_PGC">{pgcCodeOf(selectedAccount)}</DetailField>
              <DetailField icon={Coins} label="MONEDA">{selectedAccount.currency}</DetailField>
              <DetailField icon={Wallet} label="SALDO_EN_BANCO">
                {formatMoney(selectedAccount.balance, selectedAccount.currency, privacyMode)}
              </DetailField>
              <DetailField icon={Hash} label="SALDO_EN_CONTABILIDAD">
                {accountingBalance(selectedAccount) === null
                  ? noLedgerText(selectedAccount)
                  : formatMoney(accountingBalance(selectedAccount)!, selectedAccount.currency, privacyMode)}
              </DetailField>
              <DetailField icon={Scale} label="DESFASE_DE_CONCILIACIÓN">
                {accountingBalance(selectedAccount) === null
                  ? '—'
                  : formatMoney(selectedAccount.balance - accountingBalance(selectedAccount)!, selectedAccount.currency, privacyMode)}
              </DetailField>
            </DetailFieldList>
          </DetailPanel>
        ) : null}
        </div>

        {reconBankId && (
          <div className="bank-recon-area">
            <div className="bank-recon-head">
              <span className="bank-recon-title">
                Conciliación bancaria · {accounts.find(a => a.id === reconBankId)?.name || `cuenta ${reconBankId}`}
              </span>
              <button type="button" className="module-action-btn" onClick={closeRecon} data-keep-detail>Cerrar</button>
            </div>
            <BankReconciliationForm key={reconBankId} privacyMode={privacyMode} bankAccountId={reconBankId} />
          </div>
        )}

        {sepaBankId && (
          <div className="bank-recon-area">
            <div className="bank-recon-head">
              <span className="bank-recon-title">
                Cobros SEPA (Norma 19) · {accounts.find(a => a.id === sepaBankId)?.name || `cuenta ${sepaBankId}`}
                {accounts.find(a => a.id === sepaBankId)?.iban ? ` · ${accounts.find(a => a.id === sepaBankId)!.iban}` : ''}
              </span>
              <button type="button" className="module-action-btn" onClick={closeSepa} data-keep-detail>Cerrar</button>
            </div>
            <FinancialSuite
              key={sepaBankId}
              privacyMode={privacyMode}
              activeMenu="sepa"
              onChangeMenu={closeSepa}
              hideSidebar
              headerTitle="Cobros SEPA (Norma 19)"
              headerDesc="Remesas bancarias de adeudos directos"
            />
          </div>
        )}
      </BankAccountTiles>
    </div>
  );
};

export default FinanceBankAccountsView;
